import { useState } from 'react';
import { ChevronDownIcon } from '@/icons';
import styles from '@/components/ToolsPage/ToolSelect.module.css';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  /** Optional thumbnail (the table webp) shown left of the label. */
  readonly icon?: string;
}

interface ToolSelectProps {
  readonly value: string;
  readonly options: readonly SelectOption[];
  readonly placeholder: string;
  readonly onChange: (value: string) => void;
}

export function ToolSelect({
  value,
  options,
  placeholder,
  onChange,
}: ToolSelectProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={`${styles.control} ${open ? styles.controlOpen : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={styles.controlValue}>
          {selected?.icon && <img src={selected.icon} alt="" className={styles.optionIcon} />}
          <span className={selected ? styles.valueText : styles.placeholder}>
            {selected ? selected.label : placeholder}
          </span>
        </span>
        <ChevronDownIcon className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} />
      </button>

      {open && (
        <>
          <div className={styles.backdrop} onClick={() => setOpen(false)} />
          <ul className={styles.list} role="listbox">
            {options.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={opt.value === value}
                  className={`${styles.option} ${opt.value === value ? styles.optionActive : ''}`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  {opt.icon && <img src={opt.icon} alt="" className={styles.optionIcon} />}
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
